import { useState } from 'react';
import { ContentItem, ContentType, ContentStatus } from '../types';
import { useMarketingData } from '../MarketingContext';

export default function ContentCalendarPage() {
  const { companies, content } = useMarketingData();
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCompany, setFilterCompany] = useState('all');
  const [view, setView] = useState<'calendar' | 'list'>('list');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const contentTypes: ContentType[] = ['blog_post', 'website_page', 'product_page', 'landing_page', 'social_media', 'video', 'case_study', 'whitepaper', 'email_campaign'];
  const contentStatuses: ContentStatus[] = ['idea', 'briefing', 'draft', 'in_review', 'changes_required', 'approved', 'scheduled', 'published', 'needs_update', 'archived'];

  const statusColors: Record<string, string> = {
    idea: 'bg-gray-100 text-gray-600', briefing: 'bg-slate-100 text-slate-600', draft: 'bg-amber-100 text-amber-700',
    in_review: 'bg-blue-100 text-blue-700', changes_required: 'bg-red-100 text-red-700', approved: 'bg-green-100 text-green-700',
    scheduled: 'bg-purple-100 text-purple-700', published: 'bg-emerald-100 text-emerald-700', needs_update: 'bg-orange-100 text-orange-700',
    archived: 'bg-gray-100 text-gray-400',
  };

  const typeIcons: Record<string, string> = {
    blog_post: '📝', website_page: '🌐', product_page: '📦', landing_page: '🎯',
    social_media: '📱', video: '🎬', case_study: '📊', whitepaper: '📄', email_campaign: '📧',
  };

  const filtered = content.filter(c => {
    if (filterType !== 'all' && c.type !== filterType) return false;
    if (filterStatus !== 'all' && c.status !== filterStatus) return false;
    if (filterCompany !== 'all' && c.companyId !== filterCompany) return false;
    return true;
  });

  // Calendar view: group by week
  const calendarItems = filtered.filter(c => {
    if (!c.dueDate) return false;
    const d = new Date(c.dueDate);
    return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
  });

  // Weekly groups
  const weeks: { label: string; items: ContentItem[] }[] = [];
  for (let w = 0; w < 5; w++) {
    const weekStart = new Date(selectedYear, selectedMonth, w * 7 + 1);
    const weekEnd = new Date(selectedYear, selectedMonth, w * 7 + 7);
    const weekItems = calendarItems.filter(c => {
      const d = new Date(c.dueDate);
      return d >= weekStart && d <= weekEnd;
    });
    if (weekItems.length > 0) {
      weeks.push({ label: `Week ${w + 1} (${months[selectedMonth]} ${weekStart.getDate()}–${weekEnd.getDate()})`, items: weekItems });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-gray-900">Content Calendar</h1><p className="text-sm text-gray-500">Plan, schedule, and track all content across companies and channels</p></div>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button onClick={() => setView('calendar')} className={`px-3 py-1 text-xs font-medium rounded-md ${view === 'calendar' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>📅 Calendar</button>
          <button onClick={() => setView('list')} className={`px-3 py-1 text-xs font-medium rounded-md ${view === 'list' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>📋 List</button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white p-3 rounded-xl border border-gray-200">
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Types</option>{contentTypes.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Status</option>{contentStatuses.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Companies</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="flex items-center gap-1 ml-auto">
          <button onClick={() => { setSelectedMonth(m => m === 0 ? 11 : m - 1); if (selectedMonth === 0) setSelectedYear(y => y - 1); }} className="px-2 py-1 text-sm hover:bg-gray-100 rounded">◀</button>
          <span className="text-sm font-medium text-gray-700 px-2">{months[selectedMonth]} {selectedYear}</span>
          <button onClick={() => { setSelectedMonth(m => m === 11 ? 0 : m + 1); if (selectedMonth === 11) setSelectedYear(y => y + 1); }} className="px-2 py-1 text-sm hover:bg-gray-100 rounded">▶</button>
        </div>
      </div>

      {/* Status Summary */}
      <div className="flex flex-wrap gap-2">
        {contentStatuses.map(s => {
          const count = content.filter(c => c.status === s).length;
          if (count === 0) return null;
          return <span key={s} className={`text-xs px-2.5 py-1 rounded-full ${statusColors[s]}`}>{s.replace('_', ' ')}: {count}</span>;
        })}
      </div>

      {view === 'calendar' ? (
        <div className="space-y-4">
          {weeks.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-sm text-gray-400">No content scheduled for {months[selectedMonth]} {selectedYear}</div>
          ) : weeks.map((week, wi) => (
            <div key={wi} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200"><span className="text-xs font-bold text-gray-700">{week.label}</span></div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {week.items.map(item => {
                  const co = companies.find(c => c.id === item.companyId);
                  return (
                    <div key={item.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm">{typeIcons[item.type] || '📄'}</span>
                        <span className="text-xs font-medium text-gray-800 truncate">{item.title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusColors[item.status]}`}>{item.status.replace('_', ' ')}</span>
                        <span className="text-[10px] text-gray-400">{co?.name}</span>
                      </div>
                      <div className="text-[10px] text-gray-400 mt-1">Due: {item.dueDate}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => {
            const co = companies.find(c => c.id === item.companyId);
            return (
              <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
                <span className="text-lg">{typeIcons[item.type] || '📄'}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">{item.title}</div>
                  <div className="text-xs text-gray-500">{item.type.replace('_', ' ')} · {item.channel} · {co?.name}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[item.status]}`}>{item.status.replace('_', ' ')}</span>
                <div className="text-xs text-gray-400 w-24 text-right">Due: {item.dueDate || '—'}</div>
                <div className="text-xs text-gray-400 w-20 text-right">Pub: {item.publishDate || '—'}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
